import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import { usePlayerSettings, usePlayerStore } from '@cartwhl/3d/player';
import type {
  ExportType,
  KeyframeCleaningMethod,
  SceneExportSettings,
  SceneObjectExport,
} from '@cartwhl/common/api/postExternalSceneExport';
import { type FrameRate, frameRates } from '@cartwhl/common/types';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Select from '@cartwhl/ui/atoms/Select';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import InfoIcon from '@cartwhl/ui/icons/InfoIcon';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import Button from '@cartwhl/ui/input/Button';
import CircularProgressIndicator from '@cartwhl/ui/messaging/CircularProgressIndicator';
import Modal from '@cartwhl/ui/molecules/Modal';
import Tabs from '@cartwhl/ui/molecules/Tabs';

import * as exportsStore from 'stores/exportsStore';
import type { SceneExportJobStatus } from 'stores/exportsStore';
import {
  ESTIMATED_SECONDS_PER_SCENE_OBJECT,
  isSceneExportInProgress,
} from 'stores/exportsStore';

import { useStore } from '../motionEditorStore';
import type { MotionEditorScene } from '../types';
import {
  motionEditorVideoExportState,
  resetMotionEditorVideoRecording,
} from '../videoExport/motionEditorVideoExportStore';
import { runSceneVideoDownload } from '../videoExport/runSceneVideoDownload';
import SceneVideoDownloadPanel from './SceneVideoDownloadPanel';

type SceneDownloadTab = 'fbx' | 'glb' | 'maya' | 'bvh' | 'video';

type SceneExportJobView = {
  readonly status: SceneExportJobStatus;
  readonly objectReferenceCount: number;
  readonly exports: readonly SceneObjectExport[];
  readonly startedAt: string;
  readonly estimatedFinishTime: string;
  readonly downloadURL?: string;
  readonly error?: string;
  readonly lastPollError?: string;
};

const DEFAULT_FORMAT_BY_TAB: Record<
  Exclude<SceneDownloadTab, 'video'>,
  ExportType
> = {
  bvh: 'bvh',
  fbx: 'fbx-maya',
  glb: 'glb',
  maya: 'ma',
};

export default function SceneDownloadModal({
  scene,
  close,
}: {
  scene: MotionEditorScene;
  close: () => void;
}) {
  const { settings: playerSettings } = usePlayerSettings();
  const playerStore = usePlayerStore();
  const { characters } = useStore().state;
  const { isRecording: isVideoExportRecording } = useSnapshot(
    motionEditorVideoExportState,
  );
  const exportsSnapshot = useSnapshot(exportsStore.state);
  const exportJob = exportsSnapshot.sceneExports[scene.sceneID];
  const objectReferenceCount = Object.keys(scene.objects ?? {}).length;
  const isExportActive = isSceneExportInProgress(exportJob);
  const [activeTab, setActiveTab] = useState<SceneDownloadTab>('fbx');
  const [downloadFormat, setDownloadFormat] = useState<ExportType>('fbx-maya');
  const [frameRate, setFrameRate] = useState<FrameRate>(
    playerSettings.playback.frameRate,
  );
  const [keyframeCleaning, setKeyframeCleaning] =
    useState<KeyframeCleaningMethod>('simplify');
  const [ikFeet, setIkFeet] = useState(true);
  const [ikHands, setIkHands] = useState(false);

  useEffect(() => {
    setFrameRate(playerSettings.playback.frameRate);
  }, [playerSettings.playback.frameRate]);

  useEffect(() => {
    return () => {
      resetMotionEditorVideoRecording();
    };
  }, []);

  const exportSettings = useMemo<SceneExportSettings>(() => {
    const shouldCleanKeyframes = activeTab === 'fbx' || activeTab === 'maya';
    const isMayaFormat = downloadFormat === 'ma' || downloadFormat === 'mb';
    const nextSettings: SceneExportSettings = {
      exportType: downloadFormat,
      forward: '-Z',
      frameRate,
      frameStepSize: playerSettings.playback.frameStepSize,
      moveInPlace: false,
      up: 'Y',
      includeMesh: activeTab !== 'bvh',
      keyframeCleaning: shouldCleanKeyframes ? keyframeCleaning : 'none',
    };

    if (isMayaFormat) {
      nextSettings.ikFeet = ikFeet;
      nextSettings.ikHands = ikHands;
    }

    if (characters.characterSettings.faceExpression) {
      nextSettings.faceExpression = characters.characterSettings.faceExpression;
    }
    if (characters.characterSettings.staticHands) {
      nextSettings.handPose = characters.characterSettings.staticHands;
    }
    if (characters.characterSettings.skinHex !== 'default') {
      nextSettings.skinHex = characters.characterSettings.skinHex;
    }

    return nextSettings;
  }, [
    activeTab,
    characters.characterSettings.faceExpression,
    characters.characterSettings.skinHex,
    characters.characterSettings.staticHands,
    downloadFormat,
    frameRate,
    ikFeet,
    ikHands,
    keyframeCleaning,
    playerSettings.playback.frameStepSize,
  ]);

  const handleTabChange = (value: string) => {
    const nextTab = value as SceneDownloadTab;
    setActiveTab(nextTab);

    if (nextTab === 'video') {
      return;
    }

    setDownloadFormat(DEFAULT_FORMAT_BY_TAB[nextTab]);

    if (nextTab === 'bvh') {
      setKeyframeCleaning('none');
    } else if (nextTab === 'glb') {
      setKeyframeCleaning('none');
    } else {
      setKeyframeCleaning('simplify');
    }
  };

  const handleStartDownload = () => {
    if (activeTab === 'video') {
      void runSceneVideoDownload(playerStore, scene.sceneName);
      return;
    }

    void exportsStore.createSceneExport({
      sceneID: scene.sceneID,
      sceneName: scene.sceneName,
      objectReferenceCount,
      exportSettings,
    });
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title="Download"
      maxWidth={760}
      fullscreenOnMobile
      footer={
        <div className="flex items-center justify-between gap-3">
          <Button onClick={close}>Close</Button>
          <Button
            variant="primary"
            disabled={
              activeTab === 'video'
                ? isVideoExportRecording
                : isExportActive || objectReferenceCount === 0
            }
            onClick={handleStartDownload}
          >
            Download
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <SceneExportStatus
          exportJob={exportJob}
          objectReferenceCount={objectReferenceCount}
          sceneID={scene.sceneID}
        />
        <div className={isExportActive ? 'pointer-events-none opacity-60' : ''}>
          <Tabs
            defaultValue={activeTab}
            onChange={handleTabChange}
            tabListClassName="-mx-4 lg:mx-0 px-4 lg:px-0"
            tabs={[
              {
                value: 'fbx',
                label: <TabLabel>FBX</TabLabel>,
                content: (
                  <SceneExportSettingsFields
                    tab="fbx"
                    downloadFormat={downloadFormat}
                    setDownloadFormat={setDownloadFormat}
                    frameRate={frameRate}
                    setFrameRate={setFrameRate}
                    keyframeCleaning={keyframeCleaning}
                    setKeyframeCleaning={setKeyframeCleaning}
                  />
                ),
              },
              {
                value: 'bvh',
                label: <TabLabel>BVH</TabLabel>,
                content: (
                  <SceneExportSettingsFields
                    tab="bvh"
                    frameRate={frameRate}
                    setFrameRate={setFrameRate}
                  />
                ),
              },
              {
                value: 'glb',
                label: <TabLabel>glTF</TabLabel>,
                content: (
                  <SceneExportSettingsFields
                    tab="glb"
                    frameRate={frameRate}
                    setFrameRate={setFrameRate}
                  />
                ),
              },
              {
                value: 'maya',
                label: <TabLabel>Maya</TabLabel>,
                content: (
                  <SceneExportSettingsFields
                    tab="maya"
                    downloadFormat={downloadFormat}
                    setDownloadFormat={setDownloadFormat}
                    frameRate={frameRate}
                    setFrameRate={setFrameRate}
                    keyframeCleaning={keyframeCleaning}
                    setKeyframeCleaning={setKeyframeCleaning}
                    ikFeet={ikFeet}
                    setIkFeet={setIkFeet}
                    ikHands={ikHands}
                    setIkHands={setIkHands}
                  />
                ),
              },
              {
                value: 'video',
                label: <TabLabel>Video</TabLabel>,
                content: <SceneVideoDownloadPanel />,
              },
            ]}
          />
        </div>
      </div>
    </Modal>
  );
}

function SceneExportStatus({
  exportJob,
  objectReferenceCount,
  sceneID,
}: {
  exportJob?: SceneExportJobView | null;
  objectReferenceCount: number;
  sceneID: string;
}) {
  const progress = useSceneExportProgress(exportJob);
  const completedCount =
    exportJob?.exports.filter(
      (exportRecord) => exportRecord.status === 'COMPLETED',
    ).length ?? 0;
  const isFinalizingDownloadURL =
    exportJob && completedCount >= exportJob.objectReferenceCount;
  const estimatedSeconds =
    (exportJob?.objectReferenceCount ?? objectReferenceCount) *
    ESTIMATED_SECONDS_PER_SCENE_OBJECT;

  if (exportJob?.status === 'error') {
    return (
      <div className="bg-red/10 flex items-center gap-3 rounded-lg px-4 py-3">
        <WarningIcon className="text-red h-5 w-5 flex-none" />
        <Text variant="studio/body/sm">
          {exportJob.error ?? 'Scene export failed. Please try again.'}
        </Text>
      </div>
    );
  }

  if (exportJob?.status === 'ready') {
    return (
      <div className="bg-green/15 flex items-center justify-between gap-4 rounded-lg px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <CheckmarkIcon className="text-green h-5 w-5 flex-none" />
          <Text variant="studio/body/sm">
            Export ready. The download should start automatically.
          </Text>
        </div>
        <Button
          size="sm"
          onClick={() => {
            void exportsStore.downloadSceneExport(sceneID);
          }}
        >
          Download
        </Button>
      </div>
    );
  }

  if (exportJob && isSceneExportInProgress(exportJob)) {
    return (
      <div className="bg-gray-75 flex min-h-[220px] flex-col items-center justify-center rounded-lg px-8 py-8 text-white">
        <CircularProgressIndicator
          className="mx-auto mb-5"
          progress={progress}
          radius={44}
        />
        <Text as="div" variant="studio/body/sm" className="text-center">
          {isFinalizingDownloadURL
            ? 'Finalizing download link.'
            : `${exportJob.objectReferenceCount - completedCount} scene characters still downloading.`}
        </Text>
        <Text
          as="div"
          variant="studio/body/sm"
          className="mt-2 text-center text-white/70"
        >
          Estimated time: {formatDuration(estimatedSeconds)}
        </Text>
      </div>
    );
  }

  return (
    <div className="bg-gray-15 dark:bg-gray-75 flex flex-wrap items-center justify-between gap-3 rounded-lg px-4 py-3">
      <div className="flex flex-col gap-1">
        <Text variant="studio/body/medium/sm">Scene characters</Text>
        <Text
          variant="studio/body/sm"
          className="text-gray-60 dark:text-gray-30"
        >
          {objectReferenceCount} character
          {objectReferenceCount === 1 ? '' : 's'}
        </Text>
      </div>
      <div className="flex flex-col gap-1 text-right">
        <Text variant="studio/body/medium/sm">Estimate</Text>
        <Text
          variant="studio/body/sm"
          className="text-gray-60 dark:text-gray-30"
        >
          {formatDuration(estimatedSeconds)}
        </Text>
      </div>
    </div>
  );
}

function SceneExportSettingsFields({
  tab,
  downloadFormat,
  setDownloadFormat,
  frameRate,
  setFrameRate,
  keyframeCleaning,
  setKeyframeCleaning,
  ikFeet,
  setIkFeet,
  ikHands,
  setIkHands,
}: {
  tab: Exclude<SceneDownloadTab, 'video'>;
  downloadFormat?: ExportType;
  setDownloadFormat?: (format: ExportType) => void;
  frameRate: FrameRate;
  setFrameRate: (frameRate: FrameRate) => void;
  keyframeCleaning?: KeyframeCleaningMethod;
  setKeyframeCleaning?: (method: KeyframeCleaningMethod) => void;
  ikFeet?: boolean;
  setIkFeet?: (enabled: boolean) => void;
  ikHands?: boolean;
  setIkHands?: (enabled: boolean) => void;
}) {
  const checkboxId = useId();

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-6">
      {tab === 'fbx' && (
        <Field label="Software">
          <Select
            inModal
            value={downloadFormat}
            onValueChange={(value) => setDownloadFormat?.(value as ExportType)}
            items={[
              { label: 'Maya', value: 'fbx-maya' },
              { label: 'Blender', value: 'fbx-blender' },
              { label: 'Unreal', value: 'fbx-unreal' },
              { label: 'Roblox', value: 'fbx-roblox' },
            ]}
          />
        </Field>
      )}

      {tab === 'maya' && (
        <Field label="Format">
          <Select
            inModal
            value={downloadFormat}
            onValueChange={(value) => setDownloadFormat?.(value as ExportType)}
            items={[
              { label: 'Maya ASCII (.ma)', value: 'ma' },
              { label: 'Maya Binary (.mb)', value: 'mb' },
            ]}
          />
        </Field>
      )}

      <Field label="Frame rate">
        <Select
          inModal
          value={frameRate}
          onValueChange={(value) => setFrameRate(Number(value) as FrameRate)}
          items={frameRates.map((rate) => ({
            label: rate.toString(),
            value: rate,
          }))}
        />
      </Field>

      {(tab === 'fbx' || tab === 'glb') && (
        <Field label="Character">
          <div className="flex flex-1 items-center">
            <Checkbox
              id={`${checkboxId}-mesh`}
              align="center"
              checked
              disabled
              label="Include Mesh"
              variant="checkbox"
              color="settings"
              setChecked={() => {}}
            />
          </div>
        </Field>
      )}

      {(tab === 'fbx' || tab === 'maya') && (
        <Field label="Spline simplification">
          <Select
            inModal
            value={keyframeCleaning}
            onValueChange={(value) =>
              setKeyframeCleaning?.(value as KeyframeCleaningMethod)
            }
            items={[
              { label: 'None', value: 'none' },
              { label: 'Rebuild curves', value: 'simplify' },
            ]}
          />
        </Field>
      )}

      {tab === 'maya' && (
        <Field label="IK Settings">
          <div className="flex items-center gap-3">
            <Checkbox
              id={`${checkboxId}-ik-feet`}
              align="center"
              checked={ikFeet ?? false}
              color="settings"
              label="IK Feet"
              setChecked={(checked) => setIkFeet?.(checked)}
              variant="checkbox"
            />
            <Checkbox
              id={`${checkboxId}-ik-hands`}
              align="center"
              checked={ikHands ?? false}
              color="settings"
              label="IK Hands"
              setChecked={(checked) => setIkHands?.(checked)}
              variant="checkbox"
            />
          </div>
        </Field>
      )}

      <OrientationInfo
        className={tab === 'bvh' ? 'col-span-2 sm:col-span-2' : 'col-span-2'}
      />
      <div className="border-gray-30 dark:border-gray-75 col-span-2 border-t pt-6">
        <div className="bg-blue/10 dark:bg-blue/20 flex items-center gap-3 rounded-lg px-4 py-3">
          <InfoIcon className="text-blue h-5 w-5 flex-none shrink-0" />
          <Text variant="studio/body/sm" className="text-primary min-w-0">
            You will receive a .zip containing each character as a separate
            file.
          </Text>
        </div>
      </div>
    </div>
  );
}

function OrientationInfo({ className }: { className?: string }) {
  return (
    <div
      className={`border-gray-30 dark:border-gray-75 flex flex-col gap-3 border-t pt-6 pb-2 ${className ?? ''}`}
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 sm:gap-x-4 sm:gap-y-6">
        <Text
          variant="studio/body/medium/sm"
          className="col-span-2 mb-3 sm:col-span-1 sm:mb-0"
        >
          Orientation
        </Text>

        <div className="flex items-center gap-2">
          <Text className="text-gray-60" variant="studio/body/sm">
            Forward:
          </Text>
          <Text className="text-gray-60" variant="studio/body/sm">
            -Z Forward
          </Text>
        </div>

        <div className="flex items-center gap-2">
          <Text className="text-gray-60" variant="studio/body/sm">
            Up:
          </Text>
          <Text className="text-gray-60" variant="studio/body/sm">
            Y Up
          </Text>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <Text variant="studio/body/medium/sm">{label}</Text>
      {children}
    </div>
  );
}

function TabLabel({ children }: { children: ReactNode }) {
  return (
    <div className="flex w-full justify-between">
      <Text className="p-1 text-left" variant="studio/body/medium/sm">
        {children}
      </Text>
    </div>
  );
}

function useSceneExportProgress(exportJob?: SceneExportJobView | null) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!isSceneExportInProgress(exportJob)) {
      return;
    }

    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [exportJob]);

  if (!exportJob) {
    return 0;
  }

  if (exportJob.status === 'ready') {
    return 100;
  }

  const startedAt = new Date(exportJob.startedAt).getTime();
  const estimatedFinishTime = new Date(exportJob.estimatedFinishTime).getTime();
  if (Number.isNaN(startedAt) || Number.isNaN(estimatedFinishTime)) {
    return 0;
  }

  const totalDuration = Math.max(estimatedFinishTime - startedAt, 1);
  const elapsedDuration = Math.max(now - startedAt, 0);
  const progress = Math.round((100 * elapsedDuration) / totalDuration);
  return Math.min(progress, 99);
}

function formatDuration(seconds: number) {
  if (seconds <= 60) {
    return 'about 1 minute';
  }

  const minutes = Math.ceil(seconds / 60);
  return `about ${minutes} minutes`;
}
